import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ChevronDown, ChevronUp, Plus, ReceiptText, RotateCw, Save, Settings, X } from "lucide-react";
import { fetchNews } from "../services/news";
import { getToMeConversations } from "../services/chat";
import { apiFetch } from "../utils/apiFetch";
import { listClientRequests } from "../services/requests";
import { getClientBillingBreakdown, getClientBillingExpenses } from "../services/billing";
import { fetchSockets, unlockDevice } from "../services/device-access";
import { fetchClientPolls } from "../services/polls";
import { listClientSensors } from "../services/sensors";
import { API_BASE_CLIENT as API_BASE } from "../config/vars";

const WIDGET_LAYOUT_KEY = "WR_CLIENT_WIDGETS_LAYOUT";
const DEFAULT_WIDGET_ORDER = ["billing", "info", "news", "polls", "chats", "requests", "sensors", "door"];

const readWidgetLayout = () => {
    try {
        const raw = localStorage.getItem(WIDGET_LAYOUT_KEY);
        if (!raw) return { order: [...DEFAULT_WIDGET_ORDER], hidden: [] };
        const parsed = JSON.parse(raw);
        const order = Array.isArray(parsed?.order)
            ? parsed.order.filter((id) => DEFAULT_WIDGET_ORDER.includes(id))
            : [...DEFAULT_WIDGET_ORDER];
        const hidden = Array.isArray(parsed?.hidden)
            ? parsed.hidden.filter((id) => DEFAULT_WIDGET_ORDER.includes(id))
            : [];
        const missing = DEFAULT_WIDGET_ORDER.filter((id) => !order.includes(id) && !hidden.includes(id));
        return { order: [...order, ...missing], hidden };
    } catch (_) {
        return { order: [...DEFAULT_WIDGET_ORDER], hidden: [] };
    }
};

const writeWidgetLayout = (layout) => {
    try {
        localStorage.setItem(WIDGET_LAYOUT_KEY, JSON.stringify(layout));
    } catch (_) {
        // ignore
    }
};

const formatMoney = (v) => {
    const n = Number(v);
    const safe = Number.isFinite(n) ? n : 0;
    return safe.toFixed(2);
};

const toNumber = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
};

const getAccruedAmount = (item) => {
    const explicit = Number(item?.accrued_amount);
    if (Number.isFinite(explicit)) return explicit;
    return toNumber(item?.price_per_m2) * toNumber(item?.area_m2);
};

const getBalanceAmount = (item) => {
    const explicit = Number(item?.balance_amount);
    if (Number.isFinite(explicit)) return explicit;
    return getAccruedAmount(item) - toNumber(item?.paid_amount) + toNumber(item?.debt_amount);
};

const getBalancePresentation = (item) => {
    const balance = getBalanceAmount(item);
    if (balance > 0) {
        return { label: "Недоплачено", amount: balance, color: "#b42318" };
    }
    if (balance < 0) {
        return { label: "Переплачено", amount: Math.abs(balance), color: "#067647" };
    }
    return { label: "Оплачено", amount: 0, color: "#344054" };
};

const WidgetCard = ({
    title,
    value,
    hint,
    onClick,
    className = "",
    hasBadge = false,
    badgeValue = null,
    editMode = false,
    onMoveUp,
    onMoveDown,
    onHide,
    canMoveUp = false,
    canMoveDown = false,
}) => {
    const clickable = !editMode && typeof onClick === "function";
    const numericBadge = Number(badgeValue);
    const hasNumericBadge = Number.isFinite(numericBadge) && numericBadge > 0;
    const showBadge = !editMode && Boolean(hasBadge || hasNumericBadge);
    const badgeLabel = hasNumericBadge ? numericBadge : 1;
    return (
        <div className={`widget-card-shell ${editMode ? "is-editing" : ""}`}>
            <div
                className={`widget-card ${clickable ? "widget-card--click" : ""} ${className}`}
                role={clickable ? "button" : undefined}
                tabIndex={clickable ? 0 : undefined}
                onClick={clickable ? onClick : undefined}
                onKeyDown={clickable ? (event) => {
                    if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        onClick?.(event);
                    }
                } : undefined}
            >
                {showBadge ? (
                    <span className="widget-card__badge widget-card__badge--count" aria-hidden="true">
                        {badgeLabel}
                    </span>
                ) : null}
                {editMode ? (
                    <div className="widget-card-edit-bar">
                        <button type="button" className="widget-card-edit-btn" onClick={onMoveUp} disabled={!canMoveUp} title="Вище" aria-label="Вище">
                            <ChevronUp size={16} />
                        </button>
                        <button type="button" className="widget-card-edit-btn" onClick={onMoveDown} disabled={!canMoveDown} title="Нижче" aria-label="Нижче">
                            <ChevronDown size={16} />
                        </button>
                        <button type="button" className="widget-card-edit-btn widget-card-edit-btn--danger" onClick={onHide} title="Приховати" aria-label="Приховати">
                            <X size={16} />
                        </button>
                    </div>
                ) : null}
                <div className="widget-card__title">{title}</div>
                <div className="widget-card__value">{value}</div>
                {hint ? <div className="widget-card__hint">{hint}</div> : null}
            </div>
        </div>
    );
};

const ClientWidgets = ({
    onNavigate,
    hasUnreadNews = false,
    unreadNewsCount = 0,
    hasUnreadPolls = false,
    unreadPollsCount = 0,
    hasOperatorInfo = false,
    unreadOperatorInfoCount = 0,
    hasChats = false,
    unreadChatsCount = 0,
    hasRequestUpdates = false,
    sensorAccess = { page_enabled: false },
    enabledModules = null,
}) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const [doorSockets, setDoorSockets] = useState([]);
    const [doorPanelOpen, setDoorPanelOpen] = useState(false);
    const [doorMessage, setDoorMessage] = useState("");

    const [infoCount, setInfoCount] = useState(0);
    const [newsCount, setNewsCount] = useState(0);
    const [activeChatsCount, setActiveChatsCount] = useState(0);
    const [openRequestsCount, setOpenRequestsCount] = useState(0);
    const [activePollsCount, setActivePollsCount] = useState(0);
    const [accessibleSensorsCount, setAccessibleSensorsCount] = useState(0);
    const [sensorAlertsCount, setSensorAlertsCount] = useState(0);
    const [billingItems, setBillingItems] = useState([]);
    const [billingExpenseGroups, setBillingExpenseGroups] = useState([]);
    const [expandedBillingKey, setExpandedBillingKey] = useState(null);
    const [expandedExpenseKeys, setExpandedExpenseKeys] = useState({});
    const [billingOverviewOpen, setBillingOverviewOpen] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [showAddWidgetModal, setShowAddWidgetModal] = useState(false);
    const [layout, setLayout] = useState(() => readWidgetLayout());
    const [layoutHintSeen, setLayoutHintSeen] = useState(() => {
        try {
            return localStorage.getItem("WR_CLIENT_WIDGETS_HINT_SEEN") === "1";
        } catch (_) {
            return false;
        }
    });

    const persistLayout = useCallback((next) => {
        setLayout(next);
        writeWidgetLayout(next);
    }, []);

    const markLayoutHintSeen = useCallback(() => {
        setLayoutHintSeen(true);
        try {
            localStorage.setItem("WR_CLIENT_WIDGETS_HINT_SEEN", "1");
        } catch (_) {
            // ignore
        }
    }, []);

    const handleQuickUnlock = useCallback(async (socketId) => {
        setDoorMessage("");
        setError("");
        setLoading(true);
        try {
            const res = await unlockDevice(socketId);
            if (res && res.success) {
                setDoorMessage("✅ Підʼїзд відкрито");
            } else {
                setDoorMessage(`❌ ${res?.message || "Не вдалося відкрити"}`);
            }
        } catch (e) {
            setDoorMessage(`❌ ${e?.message || "Не вдалося відкрити"}`);
        } finally {
            setLoading(false);
        }
    }, []);

    const handleDoorCardClick = useCallback(async () => {
        setDoorMessage("");
        setError("");

        // Lazy-load sockets when user actually wants to open a door.
        let sockets = doorSockets;
        if (!Array.isArray(sockets) || sockets.length === 0) {
            setLoading(true);
            try {
                sockets = await fetchSockets();
                setDoorSockets(Array.isArray(sockets) ? sockets : []);
            } catch (e) {
                setError(e?.message || "Не вдалося завантажити підʼїзди");
                sockets = [];
            } finally {
                setLoading(false);
            }
        }

        if (sockets.length === 1) {
            await handleQuickUnlock(sockets[0].id);
            return;
        }

        // Multiple doors: show quick chooser panel.
        setDoorPanelOpen((v) => !v);
    }, [doorSockets, handleQuickUnlock]);

    const billingNetworkLabel = useCallback((item) => {
        return item?.network_name || "Мережа";
    }, []);

    const billingLocationLabel = useCallback((item) => {
        return [item?.street, item?.house_number, item?.location_name].filter(Boolean).join(" ").trim();
    }, []);

    const billingItemKey = useCallback((item, idx) => (
        `${item?.network_id || "n"}-${item?.location_id || "l"}-${idx}`
    ), []);

    const totalBillingBalance = useMemo(
        () => billingItems.reduce((sum, item) => sum + getBalanceAmount(item), 0),
        [billingItems]
    );

    useEffect(() => {
        setExpandedBillingKey(null);
        setExpandedExpenseKeys({});
    }, [billingItems]);

    const billingExpensesByNetwork = useMemo(() => {
        const entries = (billingExpenseGroups || []).map((item) => [Number(item?.network_id), item]);
        return Object.fromEntries(entries);
    }, [billingExpenseGroups]);

    const widgetCatalog = useMemo(() => {
        const moduleOn = (key) => enabledModules == null || enabledModules[key] !== false;
        const sensorsAvailable = Boolean(
            moduleOn("sensors") && sensorAccess.page_enabled && accessibleSensorsCount > 0
        );
        const billingAvailable = moduleOn("billing") && billingItems.length > 0;
        return {
            billing: {
                id: "billing",
                title: "До оплати",
                available: billingAvailable,
                kind: "billing",
            },
            info: {
                id: "info",
                title: "Інформація",
                value: infoCount,
                hint: "Оголошення / повідомлення оператора",
                onClick: () => onNavigate && onNavigate("operator-info"),
                badgeValue: unreadOperatorInfoCount,
                available: moduleOn("info"),
                kind: "card",
            },
            news: {
                id: "news",
                title: "Новини",
                value: newsCount,
                hint: "Всі новини для вас",
                onClick: () => onNavigate && onNavigate("news"),
                badgeValue: unreadNewsCount,
                available: moduleOn("news"),
                kind: "card",
            },
            polls: {
                id: "polls",
                title: "Опитування",
                value: activePollsCount,
                hint: "Активні опитування",
                onClick: () => onNavigate && onNavigate("polls"),
                badgeValue: unreadPollsCount,
                available: moduleOn("polls"),
                kind: "card",
            },
            chats: {
                id: "chats",
                title: "Активні переписки",
                value: activeChatsCount,
                hint: "Переписки зі статусом 'відкрито'",
                onClick: () => onNavigate && onNavigate("chat"),
                badgeValue: unreadChatsCount,
                available: moduleOn("chats"),
                kind: "card",
            },
            requests: {
                id: "requests",
                title: "Заявки",
                value: openRequestsCount,
                hint: "Відкриті заявки",
                onClick: () => onNavigate && onNavigate("requests"),
                badgeValue: hasRequestUpdates ? 1 : 0,
                available: moduleOn("requests"),
                kind: "card",
            },
            sensors: {
                id: "sensors",
                title: "Спрацювання сенсорів",
                value: sensorAlertsCount,
                hint: sensorAlertsCount > 0 ? "Доступні сенсори з тривогою або попередженням" : "Доступні сенсори без активних спрацювань",
                onClick: () => onNavigate && onNavigate("sensors"),
                className: sensorAlertsCount > 0 ? "widget-card--attention" : "",
                badgeValue: sensorAlertsCount,
                available: sensorsAvailable,
                kind: "card",
            },
            door: {
                id: "door",
                title: "Відкрити замок",
                value: doorSockets.length,
                hint: "Швидке відкривання дверей",
                onClick: handleDoorCardClick,
                available: moduleOn("door"),
                kind: "card",
            },
        };
    }, [
        infoCount, newsCount, activePollsCount, activeChatsCount, openRequestsCount, onNavigate,
        doorSockets.length, handleDoorCardClick, unreadOperatorInfoCount, unreadNewsCount,
        unreadPollsCount, unreadChatsCount, hasRequestUpdates, sensorAccess.page_enabled,
        accessibleSensorsCount, sensorAlertsCount, billingItems.length, enabledModules,
    ]);

    const visibleWidgetIds = useMemo(() => (
        (layout.order || []).filter((id) => {
            const item = widgetCatalog[id];
            return item && item.available && !(layout.hidden || []).includes(id);
        })
    ), [layout, widgetCatalog]);

    const hiddenAvailableWidgets = useMemo(() => (
        DEFAULT_WIDGET_ORDER
            .filter((id) => (layout.hidden || []).includes(id))
            .map((id) => widgetCatalog[id])
            .filter((item) => item && item.available)
    ), [layout.hidden, widgetCatalog]);

    const moveWidget = useCallback((id, direction) => {
        setLayout((prev) => {
            const hidden = prev.hidden || [];
            const visible = (prev.order || []).filter((wid) => {
                const item = widgetCatalog[wid];
                return item && item.available && !hidden.includes(wid);
            });
            const visIndex = visible.indexOf(id);
            const targetVis = visIndex + direction;
            if (visIndex < 0 || targetVis < 0 || targetVis >= visible.length) return prev;
            const order = [...(prev.order || [])];
            const from = order.indexOf(visible[visIndex]);
            const to = order.indexOf(visible[targetVis]);
            if (from < 0 || to < 0) return prev;
            [order[from], order[to]] = [order[to], order[from]];
            const next = { ...prev, order };
            writeWidgetLayout(next);
            return next;
        });
    }, [widgetCatalog]);

    const hideWidget = useCallback((id) => {
        setLayout((prev) => {
            const hidden = Array.from(new Set([...(prev.hidden || []), id]));
            const next = { ...prev, hidden };
            writeWidgetLayout(next);
            return next;
        });
    }, []);

    const showWidget = useCallback((id) => {
        setLayout((prev) => {
            const hidden = (prev.hidden || []).filter((item) => item !== id);
            const order = prev.order.includes(id) ? prev.order : [...prev.order, id];
            const next = { order, hidden };
            writeWidgetLayout(next);
            return next;
        });
        setShowAddWidgetModal(false);
    }, []);

    const resetWidgetLayout = useCallback(() => {
        const next = { order: [...DEFAULT_WIDGET_ORDER], hidden: [] };
        persistLayout(next);
        setShowAddWidgetModal(false);
        setEditMode(false);
    }, [persistLayout]);

    const load = async (aliveRef) => {
        setLoading(true);
        setError("");
        try {
            const [infoRes, news, convs, reqs, billing, expenseGroups, sockets, polls, sensors] = await Promise.all([
                apiFetch(`${API_BASE}/operator-info/client`),
                fetchNews().catch(() => []),
                getToMeConversations().catch(() => []),
                listClientRequests({ status: "open" }).catch(() => []),
                getClientBillingBreakdown().catch(() => []),
                getClientBillingExpenses().catch(() => []),
                fetchSockets().catch(() => []),
                fetchClientPolls().catch(() => []),
                sensorAccess.page_enabled ? listClientSensors().catch(() => []) : Promise.resolve([]),
            ]);

            let info = [];
            try {
                if (infoRes && infoRes.ok) info = await infoRes.json();
            } catch (_) {
                info = [];
            }

            if (aliveRef && !aliveRef.current) return;
            setInfoCount(Array.isArray(info) ? info.length : 0);
            setNewsCount(Array.isArray(news) ? news.length : 0);
            setActiveChatsCount(Array.isArray(convs) ? convs.length : 0);
            setOpenRequestsCount(Array.isArray(reqs) ? reqs.length : 0);
            setBillingItems(Array.isArray(billing) ? billing : []);
            setBillingExpenseGroups(Array.isArray(expenseGroups) ? expenseGroups : []);

            const now = Date.now();
            const activePolls = Array.isArray(polls)
                ? polls.filter((p) => p && p.status !== false && p.expires_at && new Date(p.expires_at).getTime() > now)
                : [];
            setActivePollsCount(activePolls.length);

            const normalizedSensors = Array.isArray(sensors) ? sensors : [];
            setAccessibleSensorsCount(normalizedSensors.length);
            setSensorAlertsCount(
                normalizedSensors.filter((item) => ["critical", "warning"].includes(String(item?.last_status || "").toLowerCase())).length
            );

            setDoorSockets(Array.isArray(sockets) ? sockets : []);
        } catch (e) {
            if (aliveRef && !aliveRef.current) return;
            setError(e?.message || "Не вдалося завантажити віджети");
        } finally {
            if (!aliveRef || aliveRef.current) setLoading(false);
        }
    };

    useEffect(() => {
        const aliveRef = { current: true };
        load(aliveRef);
        return () => {
            aliveRef.current = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sensorAccess.page_enabled]);

    const renderBillingBlock = (editControls = null) => (
        <section className={`widgets-billing-overview ${editMode ? "is-editing" : ""} ${billingOverviewOpen ? "is-open" : ""}`}>
            {editControls ? <div className="widgets-billing-edit-bar">{editControls}</div> : null}
            <button
                type="button"
                className="widgets-billing-overview__header widgets-billing-overview__toggle"
                onClick={() => {
                    if (editMode) return;
                    setBillingOverviewOpen((v) => !v);
                }}
                onPointerUp={(e) => e.currentTarget.blur()}
                aria-expanded={billingOverviewOpen}
                disabled={editMode}
            >
                <div>
                    <div className="widgets-billing-overview__title">
                        До оплати по мережах
                        <span className="widgets-billing-overview__caret" aria-hidden="true">
                            {billingOverviewOpen ? "▾" : "▸"}
                        </span>
                    </div>
                </div>
                <div className="widgets-billing-overview__total">
                    {totalBillingBalance > 0 ? `Недоплачено: ${formatMoney(totalBillingBalance)} грн` : totalBillingBalance < 0 ? `Переплачено: ${formatMoney(Math.abs(totalBillingBalance))} грн` : "Оплачено"}
                </div>
            </button>

            {billingOverviewOpen || editMode ? (
            <div className="widgets-billing-grid">
                {billingItems.map((item, idx) => {
                    const cardKey = billingItemKey(item, idx);
                    const expanded = !editMode && expandedBillingKey === cardKey;
                    const balanceView = getBalancePresentation(item);
                    const accruedAmount = getAccruedAmount(item);
                    const expenseGroup = billingExpensesByNetwork[Number(item?.network_id)] || null;
                    const showExpenses = Boolean(expandedExpenseKeys[cardKey]);
                    return (
                        <div
                            key={cardKey}
                            role="button"
                            tabIndex={0}
                            className={`widgets-billing-item ${expanded ? "is-active" : ""}`}
                            onClick={() => {
                                if (editMode) return;
                                setExpandedBillingKey((prev) => (prev === cardKey ? null : cardKey));
                            }}
                            onKeyDown={(event) => {
                                if (editMode) return;
                                if (event.key === "Enter" || event.key === " ") {
                                    event.preventDefault();
                                    setExpandedBillingKey((prev) => (prev === cardKey ? null : cardKey));
                                }
                            }}
                        >
                            <div className="widgets-billing-item__top">
                                <div className="widgets-billing-item__label">{billingNetworkLabel(item)}</div>
                                <div className="widgets-billing-item__toggle">{expanded ? "−" : "+"}</div>
                            </div>
                            <div className="widgets-billing-item__amount" style={{ color: balanceView.color }}>{formatMoney(balanceView.amount)} грн</div>
                            <div className="widgets-billing-item__meta" style={{ color: balanceView.color }}>
                                {expanded ? `${balanceView.label} • деталі відкриті` : balanceView.label}
                            </div>
                            {expanded ? (
                                <div className="widgets-billing-item__details">
                                    {billingLocationLabel(item) ? (
                                        <div className="widgets-billing-item__detail-row widgets-billing-item__detail-row--full">
                                            <span>Локація</span>
                                            <strong>{billingLocationLabel(item)}</strong>
                                        </div>
                                    ) : null}
                                    {item.personal_account ? (
                                        <div className="widgets-billing-item__detail-row widgets-billing-item__detail-row--full">
                                            <span>Особовий рахунок</span>
                                            <strong>{item.personal_account}</strong>
                                        </div>
                                    ) : null}
                                    <div className="widgets-billing-item__detail-row">
                                        <span>Площа</span>
                                        <strong>{formatMoney(item.area_m2)} м²</strong>
                                    </div>
                                    <div className="widgets-billing-item__detail-row">
                                        <span>Нараховано</span>
                                        <strong>{formatMoney(accruedAmount)} грн</strong>
                                    </div>
                                    <div className="widgets-billing-item__detail-row">
                                        <span>Борг</span>
                                        <strong>{formatMoney(item.debt_amount)} грн</strong>
                                    </div>
                                    <div className="widgets-billing-item__detail-row">
                                        <span>Сплачено</span>
                                        <strong>{formatMoney(item.paid_amount)} грн</strong>
                                    </div>
                                    <div className="widgets-billing-item__detail-row">
                                        <span>Тариф</span>
                                        <strong>{formatMoney(item.price_per_m2)} грн/м²</strong>
                                    </div>
                                    <div className="widgets-billing-item__detail-row widgets-billing-item__detail-row--full">
                                        <span>До оплати</span>
                                        <strong style={{ color: balanceView.color }}>{balanceView.label}: {formatMoney(balanceView.amount)} грн</strong>
                                    </div>

                                    <div className="widgets-billing-item__detail-row widgets-billing-item__detail-row--full">
                                        <div className="widgets-billing-item__expense-head">
                                            <span>Склад тарифу</span>
                                            <button
                                                type="button"
                                                className="widgets-billing-item__expense-toggle"
                                                onClick={(event) => {
                                                    event.stopPropagation();
                                                    setExpandedExpenseKeys((prev) => ({
                                                        ...prev,
                                                        [cardKey]: !prev[cardKey],
                                                    }));
                                                }}
                                            >
                                                <ReceiptText size={14} />
                                                {showExpenses ? "Сховати" : "Показати"}
                                            </button>
                                        </div>
                                        {showExpenses ? (
                                            expenseGroup?.items?.length ? (
                                                <div className="widgets-billing-expenses">
                                                    {expenseGroup.items.filter((expense) => expense?.status !== false).map((expense) => (
                                                        <div key={expense.id || `${expense.title}-${expense.position}`} className="widgets-billing-expenses__item">
                                                            <div>
                                                                <strong>{expense.title}</strong>
                                                                {expense.description ? <div className="widgets-billing-expenses__description">{expense.description}</div> : null}
                                                            </div>
                                                            <div>{formatMoney(expense.amount_per_m2)} грн/м²</div>
                                                        </div>
                                                    ))}
                                                    <div className="widgets-billing-expenses__footer">
                                                        Разом: {formatMoney(expenseGroup.expenses_price_per_m2 || expenseGroup.effective_price_per_m2 || 0)} грн/м²
                                                    </div>
                                                </div>
                                            ) : (
                                                <div className="widgets-billing-expenses__empty">
                                                    Оператор ще не заповнив деталізацію тарифу для цієї мережі.
                                                </div>
                                            )
                                        ) : null}
                                    </div>
                                </div>
                            ) : null}
                        </div>
                    );
                })}
            </div>
            ) : null}
        </section>
    );

    return (
        <div className={`widgets-page ${editMode ? "widgets-page--editing" : ""}`}>
            <div className="config-header">
                <div className="header-buttons">
                    <button
                        type="button"
                        className={`add-btn toolbar-icon-btn ${editMode ? "is-active is-save" : ""}`}
                        onClick={(e) => {
                            e.currentTarget.blur();
                            setEditMode((v) => {
                                const next = !v;
                                if (next) markLayoutHintSeen();
                                return next;
                            });
                            setShowAddWidgetModal(false);
                        }}
                        onPointerUp={(e) => e.currentTarget.blur()}
                        aria-label={editMode ? "Зберегти розташування" : "Налаштувати віджети"}
                        title={editMode ? "Зберегти" : "Налаштувати віджети"}
                        aria-pressed={editMode}
                    >
                        {editMode ? <Save size={16} aria-hidden="true" /> : <Settings size={16} aria-hidden="true" />}
                    </button>
                    {editMode ? (
                        <button
                            type="button"
                            className="add-btn toolbar-icon-btn"
                            onClick={() => setShowAddWidgetModal(true)}
                            onPointerUp={(e) => e.currentTarget.blur()}
                            aria-label="Додати віджет"
                            title="Додати віджет"
                            disabled={hiddenAvailableWidgets.length === 0}
                        >
                            <Plus size={16} aria-hidden="true" />
                        </button>
                    ) : null}
                    <button
                        type="button"
                        className="add-btn refresh toolbar-icon-btn"
                        onClick={(e) => {
                            e.currentTarget.blur();
                            load();
                        }}
                        onPointerUp={(e) => e.currentTarget.blur()}
                        disabled={loading}
                        aria-label="Оновити"
                        title="Оновити"
                    >
                        <RotateCw size={16} aria-hidden="true" />
                    </button>
                </div>
            </div>

            {editMode ? (
                <div className="widgets-edit-hint">
                    Режим налаштування: ↑↓ змінюють порядок, ✕ приховує блок. Дискетка зберігає й виходить, + додає приховані.
                </div>
            ) : !layoutHintSeen ? (
                <div className="widgets-edit-hint widgets-edit-hint--soft">
                    Налаштувати головну: кнопка шестерні ⚙ у панелі знизу — порядок, приховати або додати блоки.
                </div>
            ) : null}

            {error ? <div className="message-banner" style={{ marginBottom: 12 }}>{error}</div> : null}
            {doorMessage ? <div className="message-banner" style={{ marginBottom: 12 }}>{doorMessage}</div> : null}
            {loading ? <p>⏳ Завантаження...</p> : null}

            <div className="widgets-layout">
                {visibleWidgetIds.map((id, index) => {
                    const item = widgetCatalog[id];
                    if (!item) return null;
                    const canMoveUp = index > 0;
                    const canMoveDown = index < visibleWidgetIds.length - 1;
                    const editBar = editMode ? (
                        <div className="widget-card-edit-bar">
                            <button type="button" className="widget-card-edit-btn" onClick={() => moveWidget(id, -1)} disabled={!canMoveUp} title="Вище" aria-label="Вище">
                                <ChevronUp size={16} />
                            </button>
                            <button type="button" className="widget-card-edit-btn" onClick={() => moveWidget(id, 1)} disabled={!canMoveDown} title="Нижче" aria-label="Нижче">
                                <ChevronDown size={16} />
                            </button>
                            <button type="button" className="widget-card-edit-btn widget-card-edit-btn--danger" onClick={() => hideWidget(id)} title="Приховати" aria-label="Приховати">
                                <X size={16} />
                            </button>
                        </div>
                    ) : null;

                    if (item.kind === "billing") {
                        return (
                            <div key={id} className="widgets-layout__item widgets-layout__item--billing">
                                {renderBillingBlock(editBar)}
                            </div>
                        );
                    }

                    return (
                        <div key={id} className="widgets-layout__item">
                            <WidgetCard
                                title={item.title}
                                value={item.value}
                                hint={item.hint}
                                onClick={item.onClick}
                                className={item.className}
                                badgeValue={item.badgeValue}
                                editMode={editMode}
                                onMoveUp={() => moveWidget(id, -1)}
                                onMoveDown={() => moveWidget(id, 1)}
                                onHide={() => hideWidget(id)}
                                canMoveUp={canMoveUp}
                                canMoveDown={canMoveDown}
                            />
                        </div>
                    );
                })}
            </div>

            {doorPanelOpen && doorSockets.length > 1 && !editMode && (
                <div className="widgets-door-panel">
                    <div className="widgets-door-panel__header">
                        <strong>Оберіть підʼїзд:</strong>
                    </div>
                    <div className="widgets-door-panel__grid">
                        {doorSockets.map((s) => (
                            <button
                                key={s.id}
                                type="button"
                                className="widgets-door-panel__btn"
                                onClick={() => handleQuickUnlock(s.id)}
                                disabled={loading || !s.online}
                                title={s.online ? "Натисніть щоб відкрити" : "Віддалене відкривання недоступне, використайте локальний резерв"}
                            >
                                {(s.location_name || s.device_name)
                                    ? `${s.location_name || ""}${s.location_name && s.device_name ? " / " : ""}${s.device_name || ""}`
                                    : `Підʼїзд #${s.id}`}
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {showAddWidgetModal ? (
                <div className="modal-backdrop" onClick={() => setShowAddWidgetModal(false)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <h3>Додати віджет</h3>
                        {hiddenAvailableWidgets.length === 0 ? (
                            <p>Немає прихованих віджетів для додавання.</p>
                        ) : (
                            <div className="widgets-add-list">
                                {hiddenAvailableWidgets.map((item) => (
                                    <button
                                        key={item.id}
                                        type="button"
                                        className="widgets-add-list__btn"
                                        onClick={() => showWidget(item.id)}
                                    >
                                        <span>{item.title}</span>
                                        <Plus size={16} />
                                    </button>
                                ))}
                            </div>
                        )}
                        <div className="modal-actions">
                            <button type="button" className="add-btn add-btn--secondary" onClick={resetWidgetLayout}>
                                Скинути все
                            </button>
                            <button type="button" className="add-btn" onClick={() => setShowAddWidgetModal(false)}>
                                Закрити
                            </button>
                        </div>
                    </div>
                </div>
            ) : null}
        </div>
    );
};

export default ClientWidgets;
